import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface DateLieuStepProps {
  onComplete: (data: {
    dateEvenement: string;
    dateAlternative?: string;
    lieuReserve?: boolean;
    adresseLieu?: {
      rue: string;
      codePostal: string;
      ville: string;
      typeLieu: string;
    };
  }) => void;
}

export const DateLieuStep = ({ onComplete }: DateLieuStepProps) => {
  const [dateEvenement, setDateEvenement] = useState('');
  const [dateAlternative, setDateAlternative] = useState('');
  const [lieuReserve, setLieuReserve] = useState<boolean | null>(null);
  const [adresse, setAdresse] = useState({
    rue: '',
    codePostal: '',
    ville: '',
    typeLieu: '',
  });

  const handleSubmit = () => {
    const data: any = { dateEvenement };
    
    if (dateAlternative) data.dateAlternative = dateAlternative;
    if (lieuReserve !== null) data.lieuReserve = lieuReserve;
    if (lieuReserve && adresse.rue) {
      data.adresseLieu = adresse;
    }
    
    onComplete(data);
  };

  const canContinue = dateEvenement && (lieuReserve === null || lieuReserve === false || (lieuReserve && adresse.rue && adresse.codePostal && adresse.ville && adresse.typeLieu));

  return (
    <div className="space-y-6">
      {/* Date principale */}
      <div className="space-y-2">
        <Label htmlFor="dateEvenement">Date de l'évènement *</Label>
        <Input
          id="dateEvenement"
          type="date"
          value={dateEvenement}
          onChange={(e) => setDateEvenement(e.target.value)}
          required
        />
      </div>

      {/* Date alternative */}
      <div className="space-y-2">
        <Label htmlFor="dateAlternative">Date alternative (optionnelle)</Label>
        <Input
          id="dateAlternative"
          type="date"
          value={dateAlternative}
          onChange={(e) => setDateAlternative(e.target.value)}
        />
        <p className="text-sm text-muted-foreground">
          Une date alternative augmente vos chances de disponibilité
        </p>
      </div>

      {/* Lieu */}
      <div className="space-y-4">
        <Label>Avez-vous déjà un lieu réservé ?</Label>
        <p className="text-sm text-muted-foreground">
          Pas de souci si vous n'avez pas encore le lieu - nous travaillons avec plusieurs partenaires
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant={lieuReserve === true ? "default" : "outline"}
            onClick={() => setLieuReserve(true)}
            className="h-12"
          >
            Oui, j'ai un lieu
          </Button>
          <Button
            type="button"
            variant={lieuReserve === false ? "default" : "outline"}
            onClick={() => setLieuReserve(false)}
            className="h-12"
          >
            Non, aidez-moi
          </Button>
        </div>

        {/* Formulaire adresse si lieu réservé */}
        {lieuReserve === true && (
          <div className="space-y-4 p-4 border rounded-lg">
            <div className="space-y-2">
              <Label htmlFor="rue">Adresse *</Label>
              <Input
                id="rue"
                value={adresse.rue}
                onChange={(e) => setAdresse(prev => ({ ...prev, rue: e.target.value }))}
                placeholder="Rue, avenue..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codePostal">Code postal *</Label>
                <Input
                  id="codePostal"
                  value={adresse.codePostal}
                  onChange={(e) => setAdresse(prev => ({ ...prev, codePostal: e.target.value }))}
                  placeholder="75000"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ville">Ville *</Label>
                <Input
                  id="ville"
                  value={adresse.ville}
                  onChange={(e) => setAdresse(prev => ({ ...prev, ville: e.target.value }))}
                  placeholder="Paris"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Type de lieu *</Label>
              <Select onValueChange={(value) => setAdresse(prev => ({ ...prev, typeLieu: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez le type de lieu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appartement">Appartement</SelectItem>
                  <SelectItem value="maison">Maison</SelectItem>
                  <SelectItem value="jardin">Jardin</SelectItem>
                  <SelectItem value="salle">Salle de réception</SelectItem>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="hotel">Hôtel</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!canContinue}
        className="w-full"
      >
        Continuer
      </Button>
    </div>
  );
};